<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcAddressList',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    parent: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const container = ref(null);
  const items = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    container: container.value,
    items: items.value,
    ux: ux.value,
  }));

  const $$load_container = async () => {
    return props.parent.addresses;
  };
  const $$load_items = async () => {
    return container.value.data;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'container' },
      { name: 'items', dependsOn: ['container'] },
      { name: 'ux' },
    ],
    loaders: { container: $$load_container, items: $$load_items },
    ux: {},
    dataRefs: { container, items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );

  const act_2095089031469157_emit_x6p2jik5w_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_5bj8pefr5_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_2095089031469157_showModal_tt9uajt93_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Address',
      props: {
        parent: props.parent,
      },
    };
  };
  const act_294572930540096_emit_sq9c1ht21_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_294572930540096_close_q074s4hbd_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_294572930540096_showModal_clnvvut0h_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Address',
      props: {
        parent: props.parent,
        item: $event.scope.item,
      },
    };
  };
  const act_3365375699802249_rest_4sm3w9xnj_getActionArgs = async ($event) => {
    return {
      method: 'delete',
      container: container.value,
      resource: $event.scope.item,
    };
  };
  const act_3365375699802249_rest_4sm3w9xnj_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };

  const act_7315092382398562_reloadComponentData_rphmfjk8c =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_xvjgohbmt =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_rphmfjk8c,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_xvjgohbmt],
    event: 'reload',
  });
  const act_2095089031469157_emit_x6p2jik5w = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_x6p2jik5w_getActionArgs,
  });
  const inst_evh_2095089031469157_close_5bj8pefr5 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_x6p2jik5w,
      event: 'close',
      when: inst_evh_2095089031469157_close_5bj8pefr5_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_tt9uajt93 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_tt9uajt93_getActionArgs,
      displayName: 'create',
      events: [inst_evh_2095089031469157_close_5bj8pefr5],
    });
  const inst_evh_2095089031469157_clickPreventStop_cpq5rbx28 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_tt9uajt93,
      event: 'click.prevent.stop',
      displayName: 'create',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_cpq5rbx28],
      event: 'click.prevent.stop',
    });
  const act_294572930540096_emit_sq9c1ht21 = new actionTypes.EmitAction({
    actionArgs: act_294572930540096_emit_sq9c1ht21_getActionArgs,
  });
  const inst_evh_294572930540096_close_q074s4hbd =
    new actionTypes.EventHandlerImpl({
      action: act_294572930540096_emit_sq9c1ht21,
      event: 'close',
      when: inst_evh_294572930540096_close_q074s4hbd_getWhen,
      displayName: 'emit',
    });
  const act_294572930540096_showModal_clnvvut0h =
    new actionTypes.ShowModalAction({
      actionArgs: act_294572930540096_showModal_clnvvut0h_getActionArgs,
      events: [inst_evh_294572930540096_close_q074s4hbd],
    });
  const inst_evh_294572930540096_clickPreventStop_fux7ol2vv =
    new actionTypes.EventHandlerImpl({
      action: act_294572930540096_showModal_clnvvut0h,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_294572930540096_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_294572930540096_clickPreventStop_fux7ol2vv],
      event: 'click.prevent.stop',
    });
  const act_3365375699802249_rest_4sm3w9xnj = new actionTypes.RestAction({
    actionArgs: act_3365375699802249_rest_4sm3w9xnj_getActionArgs,
    confirm: act_3365375699802249_rest_4sm3w9xnj_getConfirm,
  });
  const inst_evh_3365375699802249_clickPreventStop_0v43daka6 =
    new actionTypes.EventHandlerImpl({
      action: act_3365375699802249_rest_4sm3w9xnj,
      event: 'click.prevent.stop',
      displayName: 'rest',
    });
  const inst_evh_3365375699802249_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3365375699802249_clickPreventStop_0v43daka6],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_xvjgohbmt,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_5bj8pefr5,
    inst_evh_2095089031469157_clickPreventStop_cpq5rbx28,
    create: inst_evh_2095089031469157_clickPreventStop_cpq5rbx28,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_294572930540096_close_q074s4hbd,
    inst_evh_294572930540096_clickPreventStop_fux7ol2vv,
    inst_evh_294572930540096_clickPreventStop,
    inst_evh_3365375699802249_clickPreventStop_0v43daka6,
    inst_evh_3365375699802249_clickPreventStop,
  };

  const evh_7315092382398562_reload_xvjgohbmt = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_xvjgohbmt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_5bj8pefr5 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_5bj8pefr5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_cpq5rbx28 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_cpq5rbx28.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_close_q074s4hbd = async (event, scope) => {
    return await actions.inst_evh_294572930540096_close_q074s4hbd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_clickPreventStop_fux7ol2vv = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_294572930540096_clickPreventStop_fux7ol2vv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_294572930540096_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3365375699802249_clickPreventStop_0v43daka6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3365375699802249_clickPreventStop_0v43daka6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3365375699802249_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3365375699802249_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ gettext('Addresses') }}
          </strong>
          <ItpButton
            :text="gettext('Add address...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!parent.isReadOnly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <BListGroupItem v-if="!items || !items.length">
          <span>
            {{ gettext('No addresses have been added yet.') }}
          </span>
        </BListGroupItem>
        <BListGroupItem
          :key="item.id"
          class="d-flex flex-column"
          v-for="item in _.orderBy(
            items,
            ['is_default', 'created_at'],
            ['desc', 'desc']
          )"
        >
          <ItpBox spacing="pb-1">
            <UcAddressCard :item="item" display-default-flag> </UcAddressCard>
            <BDropdown
              size="sm"
              toggle-class="btn-default-borderless"
              no-caret
              variant="none"
              class="ms-auto mb-auto"
              v-if="!item.locked"
            >
              <template #button-content>
                <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
              </template>
              <BDropdownItemButton
                @click.prevent.stop="
                  evh_294572930540096_clickPreventStop($event, { item })
                "
              >
                <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
              </BDropdownItemButton>
              <BDropdownItemButton
                v-if="!item.locked"
                @click.prevent.stop="
                  evh_3365375699802249_clickPreventStop($event, { item })
                "
              >
                <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
              </BDropdownItemButton>
            </BDropdown>
          </ItpBox>
          <div class="d-flex align-items-end">
            <ItpText muted small>
              {{ item.created_at || datetime(item.created_at) }}
            </ItpText>
            <small class="text-muted text-nowrap mx-2">
              {{ item.updated_by || username(item.created_by) }}
            </small>
            <div
              class="ms-2 d-flex align-self-end flex-grow-1 justify-content-end"
            >
              <ItpIcon
                :title="gettext('Locked')"
                name="fa-lock"
                class="text-muted"
                v-if="item.locked"
              >
              </ItpIcon>
            </div>
          </div>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
